/**
 * Share data structure from config.data.share (decoded from fbshare URL parameter)
 */
export interface ShareData {
  firstName?: string;
  lastName?: string;
  name?: string;
  [key: string]: any;
}

/**
 * Referral object that may contain influencer indicators
 */
export interface ReferralWithInfluencer {
  influencerId?: string;
  channel?: string;
  [key: string]: any;
}

/**
 * Influencer properties extracted from share data
 */
export interface InfluencerProperties {
  firstName?: string;
  lastName?: string;
}

/**
 * Advocate and influencer properties extracted from share data
 */
export interface AdvocateAndInfluencerProperties {
  advocate: ShareData;
  influencer: InfluencerProperties;
}

/**
 * Checks if referral object indicates this is an influencer share
 */
function hasReferralInfluencerIndicator(
  referral?: ReferralWithInfluencer
): boolean {
  if (!referral) {
    return false;
  }

  return !!(referral.influencerId || referral.channel === "influencer");
}

/**
 * Checks if share data indicates this is an influencer share
 * Influencers have lastName, advocates have name
 */
function hasShareDataInfluencerIndicator(configShareData: ShareData): boolean {
  if (configShareData.name) {
    return false; // Advocates have 'name', so this is not an influencer
  }
  // If lastName is present, it's an influencer
  if (configShareData.lastName) {
    return true;
  }

  // If only firstName is present (no name, no lastName), treat as influencer for backward compatibility
  return !!configShareData.firstName;
}

/**
 * Populates influencer properties from share data
 */
function populateInfluencerProperties(
  configShareData: ShareData,
  influencer: InfluencerProperties
): void {
  if (configShareData.firstName) {
    influencer.firstName = configShareData.firstName;
  }
  if (configShareData.lastName) {
    influencer.lastName = configShareData.lastName;
  }
}

/**
 * Extracts advocate and influencer properties from share data.
 *
 * This function determines if the share is from an influencer by checking:
 * 1. The referral object for influencerId or channel === "influencer" (most reliable)
 * 2. The share data for lastName presence (fallback - influencers have lastName, advocates have name)
 *
 * @param configShareData - The share data from config.data.share (populated by merchant SDK)
 * @param referral - Optional referral object that may contain influencerId or channel
 * @returns An object with advocate and influencer properties
 */
export function extractInfluencerAndAdvocate(
  configShareData: ShareData,
  referral?: ReferralWithInfluencer
): AdvocateAndInfluencerProperties {
  // For v1 widgets, configShareData might contain channel share objects (facebook, messenger, etc.)
  // in addition to the decoded fbshare data (firstName, lastName, name).
  // The share data should be populated by the merchant SDK from the URL's fbshare parameter.

  // Use config data directly - it's already validated and sanitized by the merchant SDK
  const shareData = { ...configShareData };

  // Advocate gets all share data (including channel objects for backward compatibility)
  const advocate = { ...shareData };
  const influencer: InfluencerProperties = {};

  const isInfluencerShare =
    hasReferralInfluencerIndicator(referral) ||
    hasShareDataInfluencerIndicator(shareData);

  if (isInfluencerShare) {
    populateInfluencerProperties(shareData, influencer);
  }

  return { advocate, influencer };
}
